/**
 * Deciding whether something in the fridge counts as a recipe's ingredient.
 *
 * Inventory names are whatever the user or the camera wrote — "Chicken thighs",
 * "Basmati rice", "Extra virgin olive oil". Recipes ask for "chicken", "rice",
 * "olive oil". This bridges the two without a dictionary.
 */

import type { FoodItem } from "../food/types";

/**
 * Words for the same thing, mapped to one canonical token.
 *
 * String matching alone told someone to buy eggplant while an aubergine sat in
 * the fridge, and that is not a rounding error — it is the app being wrong
 * about the one thing it exists to know. It stops being survivable the moment
 * a shopping list is built on the same question, because a wrong "you're
 * missing this" gets acted on in a shop.
 *
 * **Strictly one token to one token.** Synonyms are applied per word, after
 * the text has been split, so a multi-word key like "olive oil" or "ground
 * beef" could never fire — it would sit in the table looking correct and
 * matching nothing, which is worse than not being there. Every entry below is
 * one word in and one word out, and a test walks the table to keep it that way.
 *
 * Applied after singularisation, so only the singular form needs an entry.
 */
export const SYNONYMS: Record<string, string> = {
  // British ↔ American, which is where the real collisions are: a recipe
  // written one way and a shop that labels things the other.
  eggplant: "aubergine",
  zucchini: "courgette",
  cilantro: "coriander",
  arugula: "rocket",
  garbanzo: "chickpea",
  capsicum: "pepper",
  cornflour: "cornstarch",
  rutabaga: "swede",
  scallion: "springonion",
  prawn: "shrimp",
  yoghurt: "yogurt",
  chilli: "chili",
  chile: "chili",
  soya: "soy",
  // Same thing, different word on the packet.
  broth: "stock",
  bouillon: "stock",
  aubergine: "aubergine",
  mince: "beef",
  passata: "tomato",
};

/**
 * Things nearly every kitchen has, which a recipe should stop calling missing.
 *
 * Salt, pepper, oil and water on every shopping list, every week, is how a
 * useful list becomes one nobody reads. The cost of assuming is small and
 * self-correcting: someone genuinely out of salt notices in the ten seconds
 * before they cook, whereas a list crying wolf gets ignored permanently.
 *
 * Deliberately short. Everything here has to be something a kitchen without it
 * is unusual, not merely something common — flour and butter are common and are
 * not on this list, because running out of either really does change dinner.
 */
const STAPLES = new Set([
  "salt",
  "pepper",
  "water",
  "oil",
  "oliveoil",
  "vegetableoil",
]);

/**
 * Words too common to carry meaning. Without this, a recipe asking for "olive
 * oil" would match anything with "oil" in it and, worse, "of" or "and" would
 * match nearly everything.
 */
const STOP_WORDS = new Set([
  "a", "an", "and", "of", "the", "with", "in", "fresh", "free", "range",
  "organic", "extra", "virgin", "whole", "large", "small", "baby",
]);

/**
 * Reduces a name to comparable words: lowercase, letters only, no filler, and
 * a crude singular. "Tomatoes" and "tomato" have to meet somewhere.
 */
export function tokenise(text: string): string[] {
  return text
    .toLowerCase()
    .replace(/[^a-z\s]/g, " ")
    .split(/\s+/)
    .filter((word) => word.length > 2 && !STOP_WORDS.has(word))
    .map(singular)
    // Synonyms last: only the singular form needs an entry, and both sides of
    // a comparison arrive here, so it doesn't matter which spelling was typed.
    .map((word) => SYNONYMS[word] ?? word);
}

/**
 * True when this is something a kitchen is assumed to have.
 *
 * Checked on the ingredient's tokens joined together, so "olive oil" is a
 * staple while "oil" alone also is, and "truffle oil" is not.
 */
export function isStaple(ingredient: string): boolean {
  const words = tokenise(ingredient);
  if (words.length === 0) return false;
  return STAPLES.has(words.join("")) || (words.length === 1 && STAPLES.has(words[0]));
}

/**
 * A crude singular. It only has to be consistent, not correct: both sides of a
 * comparison go through it, so "tomatoes" and "tomato" meeting at "tomato"
 * matters more than "tomato" being the real word.
 *
 * What it must not do is mangle a word that was never plural — "hummu" and
 * "watercres" match nothing, and the item silently stops counting.
 */
function singular(word: string): string {
  if (!word.endsWith("s")) return word;

  // Double-s stems take "es": glasses → glass, dresses → dress.
  if (word.endsWith("sses")) return word.slice(0, -2);

  // Never plurals: hummus, couscous, asparagus, citrus, watercress, cress.
  if (word.endsWith("us") || word.endsWith("ss")) return word;

  if (word.endsWith("ies") && word.length > 4) return `${word.slice(0, -3)}y`;
  if (word.endsWith("oes") && word.length > 4) return word.slice(0, -2);

  return word.length > 3 ? word.slice(0, -1) : word;
}

/**
 * True when this item satisfies this ingredient.
 *
 * Every word of the ingredient must appear in the item's name, so "olive oil"
 * needs both words and won't be satisfied by sunflower oil, while "chicken"
 * is happy with "chicken thighs". Deliberately one-directional: an item may be
 * more specific than the ingredient, never less.
 */
export function itemSatisfies(item: FoodItem, ingredient: string): boolean {
  const wanted = tokenise(ingredient);
  if (wanted.length === 0) return false;

  const have = new Set(tokenise(item.name));
  return wanted.every((word) => have.has(word));
}

/** The first item satisfying this ingredient, or null. */
export function findMatch(items: FoodItem[], ingredient: string): FoodItem | null {
  return items.find((item) => itemSatisfies(item, ingredient)) ?? null;
}

/**
 * What a recipe needs that you haven't got — the question both the recipe
 * screen and the shopping list are really asking.
 *
 * Staples are never missing. That's the whole point of the list above: a
 * shopping list with salt on it every week is one nobody reads.
 */
export function missingIngredients(items: FoodItem[], ingredients: string[]): string[] {
  return ingredients.filter(
    (ingredient) => !isStaple(ingredient) && !findMatch(items, ingredient)
  );
}
